// src/components/contracts/contractModel.js
//
// Contracts are NOT rendered by MSP OS. Unlike Proposals — which need
// pixel-perfect DocRaptor/Prince output and are edited right up to send — a
// contract is a static legal document that must not be editable at send time.
// The real PDFs live in DocuSign's Template Library, tagged with DocuSign's own
// field tool. MSP OS only orchestrates: which template applies, what values go
// in the tags, send the envelope, track the status.
//
// That is why there is no renderer here and no print CSS. If a future change
// starts wanting one, that is the signal something has been misunderstood.
//
// React-free on purpose so it can be unit-tested and imported anywhere.

/* A ContractTemplate carries the legal document ITSELF plus the schema of what
   must be filled to send it. `content` is HTML with {{fieldKey}} placeholders,
   rendered to PDF at send time — a template cannot go active while it is empty,
   because there would be nothing to sign. */
export function makeContractTemplate(extra = {}) {
  const now = Date.now();
  return {
    id: `ct-${now}-${Math.random().toString(36).slice(2, 7)}`,
    name: "",
    // [] means tier-agnostic — an ancillary contract like a 3CX agreement that
    // can accompany any tier. NOT "applies to nothing".
    appliesToTiers: [],
    // "managed-services" | "managed-devices". Bronze's contract is genuinely a
    // different document — device-priced only, no per-user row, and User
    // Management / Network / CRM reduced to hourly-rate statements — not a
    // repriced Managed Services SOW.
    documentType: "managed-services",
    // The legal document, as HTML with {{fieldKey}} merge placeholders.
    content: "",
    fieldSchema: [],
    status: "draft",              // "draft" | "active" — draft is not selectable
    createdAt: now,
    updatedAt: now,
    ...extra,
  };
}

export const FILLED_BY = ["sender", "computed", "recipient"];

export function makeFieldDef(extra = {}) {
  return {
    key: "",
    label: "",
    type: "text",                 // text | email | date | currency | number
    /* WHO supplies the value — three genuinely different things:
       "sender"    Craig fills it before sending; merge-substituted into the PDF.
       "computed"  derived from the won Proposal (pricing); never typed, shown
                   read-only so the figures can be checked before sending.
       "recipient" the CUSTOMER types it live in DocuSign during signing. Never
                   merge-substituted, never shown in the send form — it stays an
                   anchor in the PDF and becomes a real DocuSign text tab.
                   Cyber insurance is the customer's own information; pre-filling
                   it would be printing a guess on a contract. */
    filledBy: "sender",
    // Name of a Proposal `content` field to pre-populate from, or null for a
    // value that only exists at contract time (street address, commencement
    // date, insurance value). Pre-population is a STARTING POINT — the send
    // form stays editable, so an auto-filled value is never forced.
    autoFillFrom: null,
    required: false,
    ...extra,
  };
}

export const FIELD_TYPES = ["text", "email", "date", "currency", "number"];

/* One record per SEND. Envelopes are deliberately separate — a client signs the
   SOW and the 3CX agreement independently — so a proposal with two contracts
   has two instances, two envelopes and two statuses.

   `proposalId` is a pointer, matching quote.proposalId and
   proposal.opportunityId. The Proposal deliberately carries no list of its
   contracts: a list on one side and a pointer on the other is two copies of one
   fact and they drift. */
export function makeContractInstance({ proposalId, contractTemplateId, fieldValues = {} } = {}) {
  const now = Date.now();
  return {
    id: `ci-${now}-${Math.random().toString(36).slice(2, 7)}`,
    proposalId: proposalId || null,
    contractTemplateId: contractTemplateId || null,
    // Snapshot of the template's name at send time. The template can be renamed
    // or retired later; a sent contract must still say what it was.
    templateNameAtSend: "",
    fieldValues,
    docuSignEnvelopeId: null,
    status: "draft",
    sentAt: null,
    signedAt: null,
    lastStatusCheckAt: null,
    lastError: null,
    createdAt: now,
    updatedAt: now,
  };
}

/* DocuSign's envelope vocabulary mapped to this project's. Kept HERE rather
   than in the UI so both the backend and the frontend agree on what
   "completed" means. DocuSign says "completed"; a human says "signed". */
export const DOCUSIGN_STATUS_MAP = {
  created: "draft",
  sent: "sent",
  delivered: "delivered",
  completed: "signed",
  signed: "signed",
  declined: "declined",
  voided: "voided",
};

export const CONTRACT_STATUS_META = {
  draft:     { label: "Draft",     bg: "#f2f2f2", fg: "#777" },
  sent:      { label: "Sent",      bg: "#eaf2f8", fg: "#2471a3" },
  delivered: { label: "Delivered", bg: "#eaf2f8", fg: "#2471a3" },
  signed:    { label: "Signed",    bg: "#eefaef", fg: "#1e7d34" },
  declined:  { label: "Declined",  bg: "#fdeeee", fg: "#c0392b" },
  voided:    { label: "Voided",    bg: "#f2f2f2", fg: "#777" },
};

/** Contracts belonging to one proposal. Filtered every render — never stored. */
export function contractsForProposal(instances, proposalId) {
  if (!proposalId) return [];        // an undefined id must not match undefined
  return (instances || []).filter((c) => String(c.proposalId) === String(proposalId));
}

/* Templates offered when sending, matching-tier first.

   Tier-agnostic templates ([]) are ALWAYS offered — that is the 3CX case — and
   sort after the matching tier but before other tiers' SOWs. Draft templates
   are excluded entirely: one with no content would have nothing to render. */
export function templatesForProposal(templates, templateKey) {
  const active = (templates || []).filter((t) => t.status === "active");
  const rank = (t) => {
    const tiers = Array.isArray(t.appliesToTiers) ? t.appliesToTiers : [];
    if (templateKey && tiers.includes(templateKey)) return 0;   // this proposal's tier
    if (!tiers.length) return 1;                                // ancillary, any tier
    return 2;                                                   // another tier's SOW
  };
  return [...active].sort((a, b) => rank(a) - rank(b) || String(a.name).localeCompare(String(b.name)));
}

/** Seed the send form: autoFillFrom pulls from the proposal's content. */
export function initialFieldValues(template, proposalContent = {}) {
  const out = {};
  (template?.fieldSchema || []).forEach((f) => {
    if (!f.key) return;
    // Recipient fields are the customer's to fill during signing — seeding a
    // blank for them here would put them in the send form by accident.
    if (f.filledBy === "recipient") return;
    if (f.filledBy === "computed") return;      // derived at send time, never seeded
    // An itemList holds an ARRAY. Seeding "" would make the editor render a
    // string and String(values[k]).trim() would then read "" as unfilled.
    if (f.type === "itemList") { out[f.key] = []; return; }
    const src = f.autoFillFrom;
    const auto = src && proposalContent[src] != null ? String(proposalContent[src]) : "";
    out[f.key] = auto || (f.defaultValue != null ? String(f.defaultValue) : "");
  });
  return out;
}

/* What stops a send. Returned as a list so the form can name every problem at
   once rather than failing one at a time. */
export function validateSend(template, values) {
  const problems = [];
  if (!template) return ["No contract template selected."];
  if (!String(template.content || "").trim()) {
    problems.push("This template has no document content — add it in Contract Templates.");
  }
  /* A literal {{placeholder}} would print into a signed legal document, so an
     unfilled one blocks the send rather than being rendered.

     ⚠ Only SENDER placeholders are the sender's to fill, so only those can be
     "missing" here. Two whole classes are absent from `values` BY DESIGN and
     must not be flagged:
       • recipient — the customer types these in DocuSign during signing.
       • computed  — pricing derived from the won proposal, merged in by the
                     BACKEND at send time (mergeValues -> pricingFieldValues).
     This check excluded recipient but not computed, so every send was blocked
     with "Not every field is filled: devicePrice, deviceCount, deviceSubtotal,
     onboardingRate, onboardingSubtotal, grandSubtotal, tax, total" — eight
     fields with no input on the form and no way for anyone to satisfy them.
     The required-field loop below already excluded both; this one had been
     missed when the sender/computed/recipient split landed.

     An UNCLASSIFIED placeholder (in the content, absent from fieldSchema) is
     still flagged, and should be: nothing will ever fill it, so it would print
     raw on the contract.

     The BACKEND's equivalent check deliberately does NOT exclude computed —
     see contractRenderer.unfilledPlaceholders. The two are asking different
     questions: this one asks "has the human supplied their part", the server
     one asks "is every placeholder resolved in what we are about to render",
     which is the last point a missing {{total}} can be caught. */
  const notSenderSupplied = new Set((template.fieldSchema || [])
    .filter((f) => f.filledBy === "recipient" || f.filledBy === "computed")
    .map((f) => f.key));
  const unfilled = [...new Set(
    [...String(template.content || "").matchAll(/\{\{(\w+)\}\}/g)].map((m) => m[1])
  )].filter((k) => !notSenderSupplied.has(k) && !String(values?.[k] ?? "").trim());
  if (unfilled.length) problems.push(`Not every field is filled: ${unfilled.join(", ")}.`);
  (template.fieldSchema || []).forEach((f) => {
    // Recipient and computed fields are never the sender's to supply.
    if (f.filledBy === "recipient" || f.filledBy === "computed") return;
    // Appendix 3 lists are optional by design — a contract may legitimately
    // include no printers, and an empty category simply renders nothing.
    if (f.type === "itemList") return;
    const v = String(values?.[f.key] ?? "").trim();
    if (f.required && !v) problems.push(`${f.label || f.key} is required.`);
    if (f.type === "email" && v && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) {
      problems.push(`${f.label || f.key} is not a valid email address.`);
    }
  });
  return problems;
}

/** The recipient DocuSign will address. Falls back across the email-ish fields. */
export function recipientFrom(template, values, proposalContent = {}) {
  const emailField = (template?.fieldSchema || []).find((f) => f.type === "email");
  const email = String((emailField && values?.[emailField.key]) || proposalContent.customerEmail || "").trim();
  const first = String(values?.clientFirstName || proposalContent.customerContact || "").trim();
  const last = String(values?.clientLastName || "").trim();
  const name = [first, last].filter(Boolean).join(" ")
    || String(proposalContent.customerFullName || proposalContent.customerName || "").trim();
  return { email, name };
}

/* The three managed-services SOWs, seeded as DRAFT.

   Seeded with EMPTY content: the legal text is fetched from the backend
   (/seed-content) rather than bundled, so the seed cannot carry it. Draft is
   the honest state until that text has been loaded and read. Activation
   requires content and nothing else — there is no DocuSign-side precondition
   under v2. */
export function DEFAULT_CONTRACT_TEMPLATES() {
  const base = [
    { key: "bronze-sentinel", name: "Managed Devices SOW — Bronze Sentinel" },
    { key: "silver-guardian", name: "Managed Services SOW — Silver Guardian" },
    { key: "gold-fortress",   name: "Managed Services SOW — Gold Fortress" },
    /* ⚠ appliesToTiers [] — the TIER-AGNOSTIC slot. Copper accompanies any
       tier and is signed as its own envelope, so it is offered on every
       proposal and sorts below that proposal's own SOW.
       That slot is real and intentional but had never been used: every 3CX
       reference in this file is a comment using it as a worked example, and
       all three SOWs above set [b.key]. Copper is its first real user, so
       treat the path as untested rather than proven. */
    { key: "copper-defender", name: "Managed Services SOW — Copper Defender",
      tiers: [], documentType: "copper-devices" },
  ];
  return base.map((b, i) => makeContractTemplate({
    id: `ct-seed-${b.key}`,
    name: b.name,
    ...(b.documentType ? { documentType: b.documentType } : {}),
    appliesToTiers: b.tiers || [b.key],
    fieldSchema: DEFAULT_SOW_FIELDS(),
    status: "draft",
    createdAt: Date.now() + i,
    updatedAt: Date.now() + i,
  }));
}

/* The managed-services field set. A 3CX or other ancillary contract will have a
   genuinely different one — extension counts, handset models — which is why the
   schema is per-template data rather than a fixed shape in code. */
export function DEFAULT_SOW_FIELDS() {
  return [
    // ── sender: Craig fills these before sending ──────────────────────────
    makeFieldDef({ key: "clientCompany",       label: "Client Company Name",   type: "text",  filledBy: "sender", autoFillFrom: "customerName", required: true }),
    makeFieldDef({ key: "clientContactEmail",  label: "Client Contact Email",  type: "email", filledBy: "sender", autoFillFrom: "customerEmail", required: true }),
    makeFieldDef({ key: "clientStreetAddress", label: "Client Street Address", type: "text",  filledBy: "sender" }),
    makeFieldDef({ key: "clientCity",          label: "Client City",           type: "text",  filledBy: "sender" }),
    makeFieldDef({ key: "clientPostalCode",    label: "Client Postal Code",    type: "text",  filledBy: "sender" }),
    makeFieldDef({ key: "clientPhone",         label: "Client Phone",          type: "text",  filledBy: "sender" }),
    /* SENDER, not recipient — a deliberate departure from the brief's field list.
       The brief marked clientFirstName/clientLastName "recipient", but these are
       the PRIMARY CONTACT in section 2's address block, not the signatory. A
       recipient field stays an un-substituted {{placeholder}} in the rendered
       PDF, so marking these recipient would print "{{clientFirstName}}" as
       visible text in the customer's own contact details — recipient fields only
       work where an invisible ANCHOR sits, and section 2 has none.
       The customer's SIGNATORY name and title are separately recipient-filled
       via /customer_name/ and /customer_title/, which is what the brief was
       actually protecting. Craig already knows the contact name; it auto-fills
       from the proposal. */
    makeFieldDef({ key: "clientFirstName",     label: "Client First Name",     type: "text",  filledBy: "sender", autoFillFrom: "customerContact" }),
    makeFieldDef({ key: "clientLastName",      label: "Client Last Name",      type: "text",  filledBy: "sender" }),
    makeFieldDef({ key: "commencementDate",    label: "Commencement Date",     type: "date",  filledBy: "sender", required: true }),
    makeFieldDef({ key: "supplierSignatoryName",  label: "Supplier Signatory Name",  type: "text", filledBy: "sender", defaultValue: "Craig Grant" }),
    makeFieldDef({ key: "supplierSignatoryTitle", label: "Supplier Signatory Title", type: "text", filledBy: "sender", defaultValue: "Director" }),
    // The ONE free-typed pricing input. Everything else derives from the
    // proposal, so the discount is the only lever on the outcome.
    makeFieldDef({ key: "discount", label: "Discount", type: "currency", filledBy: "sender", defaultValue: "0" }),

    /* ── Appendix 3 inventory. type "itemList" holds an ARRAY, not a string:
       [{ qty, description }] for hardware, [{ description }] for software.
       Rendered into real table markup by a separate {{TABLE:key}} pass, so
       these never go through text substitution. `hasQuantity` false on
       software — an application has no unit count. */
    makeFieldDef({ key: "desktopLaptopItems",  label: "Desktop/Laptop Devices",  type: "itemList", filledBy: "sender", hasQuantity: true }),
    makeFieldDef({ key: "firewallSwitchItems", label: "Firewall/Switch Devices", type: "itemList", filledBy: "sender", hasQuantity: true }),
    makeFieldDef({ key: "printerItems",        label: "Printers",                type: "itemList", filledBy: "sender", hasQuantity: true }),
    makeFieldDef({ key: "softwareItems",       label: "Software/Applications",   type: "itemList", filledBy: "sender", hasQuantity: false }),

    // ── recipient: the CUSTOMER types these in DocuSign, during signing ───
    makeFieldDef({ key: "customerSignatoryName",  label: "Customer Signatory Name",  type: "text",     filledBy: "recipient" }),
    makeFieldDef({ key: "customerSignatoryTitle", label: "Customer Signatory Title", type: "text",     filledBy: "recipient" }),
    makeFieldDef({ key: "cyberInsuranceValue",    label: "Cyber Insurance Value",    type: "currency", filledBy: "recipient" }),

    // ── computed: from the won Proposal. Read-only in the send form. ──────
    makeFieldDef({ key: "deviceCount",     label: "Device Count",    type: "number",   filledBy: "computed" }),
    makeFieldDef({ key: "userCount",       label: "User Count",      type: "number",   filledBy: "computed" }),
    makeFieldDef({ key: "devicePrice",     label: "Device Price",    type: "currency", filledBy: "computed" }),
    makeFieldDef({ key: "userPrice",       label: "User Price",      type: "currency", filledBy: "computed" }),
    makeFieldDef({ key: "deviceSubtotal",  label: "Device Subtotal", type: "currency", filledBy: "computed" }),
    makeFieldDef({ key: "userSubtotal",    label: "User Subtotal",   type: "currency", filledBy: "computed" }),
    /* ⚠ The device onboarding fee. These two were MISSING from this list while
       the managed-devices contract content has always carried
       {{onboardingRate}} and {{onboardingSubtotal}}, so validateSend saw two
       placeholders it could not classify, treated them as the sender's to fill,
       and blocked every Bronze send with "Not every field is filled:
       onboardingRate, onboardingSubtotal" — two fields with no input on the
       form, because a field absent from fieldSchema renders nowhere.

       ebcf7b7 fixed this symptom for the six computed fields that WERE in the
       schema and named these two in its own commit message without adding
       them, so the cure covered six of the eight fields it listed. Bronze has
       therefore never been sendable; Silver and Gold were unaffected, because
       only the managed-devices content carries these placeholders, which is
       why it went unnoticed.

       They are genuinely computed, not sender input: contractRenderer.js
       derives them at send time (deviceCount × $120 on device-only contracts,
       0 otherwise) and merges them into the render values, and the send form
       already SHOWS the resulting line as "Device onboarding (one-off)". On a
       5-device Bronze contract the fee is $600 of a $929.50 total, so removing
       the placeholders to unblock the send would have stripped two thirds of
       the contract's value out of a signed legal document. */
    /* Copper's own pricing, on its own document. Classified computed like
       every other pricing field — an UNCLASSIFIED placeholder blocks every
       send, which is exactly the outage the onboarding fee fields caused.
       Held in the shared field set rather than a fourth divergent one: the
       other three templates simply never reference these placeholders, and
       backfillFieldSchema carries them onto stored templates for free. */
    makeFieldDef({ key: "copperDeviceCount", label: "Copper Device Count",        type: "number",   filledBy: "computed" }),
    makeFieldDef({ key: "copperRate",        label: "Copper Device Rate",         type: "currency", filledBy: "computed" }),
    makeFieldDef({ key: "copperSubtotal",    label: "Copper Device Subtotal",     type: "currency", filledBy: "computed" }),
    makeFieldDef({ key: "onboardingRate",     label: "Device Onboarding Rate",     type: "currency", filledBy: "computed" }),
    makeFieldDef({ key: "onboardingSubtotal", label: "Device Onboarding Subtotal", type: "currency", filledBy: "computed" }),
    makeFieldDef({ key: "grandSubtotal",   label: "Subtotal",        type: "currency", filledBy: "computed" }),
    makeFieldDef({ key: "tax",             label: "Tax (GST 10%)",   type: "currency", filledBy: "computed" }),
    makeFieldDef({ key: "total",           label: "Total",           type: "currency", filledBy: "computed" }),
  ];
}

/** Fields grouped by who supplies them — what the send form renders from. */
export function fieldsByFilledBy(template) {
  const out = { sender: [], computed: [], recipient: [] };
  (template?.fieldSchema || []).forEach((f) => {
    const k = out[f.filledBy] ? f.filledBy : "sender";   // unclassified = sender
    out[k].push(f);
  });
  return out;
}

/* Same shape of trap as mergeTemplates() for proposal templates: the seed only
   runs when nothing is stored, so a template added to the seed later would
   never reach an existing blob. Adds new seeds, never overwrites a stored one —
   a stored record carries Craig's DocuSign IDs and edited schema. */
export function mergeContractTemplates(stored, defaults = DEFAULT_CONTRACT_TEMPLATES()) {
  if (!Array.isArray(stored) || !stored.length) return defaults;
  const byId = new Set(stored.map((t) => t.id));
  return [...stored.map(backfillFieldSchema), ...defaults.filter((d) => !byId.has(d.id))];
}

/* ⚠ Bring a STORED template's fieldSchema up to the current definition.
 *
 * Changing DEFAULT_SOW_FIELDS in code does NOT reach templates already in the
 * blob — the seed only runs when nothing is stored. That is the same trap
 * CLAUDE.md records for proposal templates ("activating a tier in code does
 * not reach existing blobs"), and it bit here: templates seeded BEFORE the
 * sender/computed/recipient split carry ten fields with no `filledBy` and no
 * computed entries at all. The consequences were not subtle —
 *   • every computed placeholder counted as unclassified, so validateSend
 *     blocked every send with "Not every field is filled: devicePrice,
 *     deviceCount, … tax, total";
 *   • cyberInsuranceValue defaulted to sender and appeared on the send form,
 *     where the whole design says the customer fills it during signing.
 *
 * Additive only, exactly like mergeTemplates' defaultContent backfill:
 * missing keys are ADDED, a missing `filledBy` ADOPTS the default
 * classification, and a value the user has already set is NEVER overwritten.
 * Classification is ours, not the user's — there is no legitimate reason for a
 * stored template to disagree with the model about which fields the system
 * computes. */
export function backfillFieldSchema(t) {
  const defs = DEFAULT_SOW_FIELDS();
  const byKey = new Map(defs.map((d) => [d.key, d]));
  const stored = Array.isArray(t?.fieldSchema) ? t.fieldSchema : [];
  const seen = new Set();
  const merged = stored.map((f) => {
    if (!f || !f.key) return f;
    seen.add(f.key);
    const d = byKey.get(f.key);
    if (!d) return f;                                   // user-added field: leave alone
    return { ...f, filledBy: f.filledBy || d.filledBy }; // only fill the gap
  });
  defs.forEach((d) => { if (!seen.has(d.key)) merged.push(d); });
  return { ...t, fieldSchema: merged };
}

/* ⚠ Bring a STORED template's CONTENT up to date with a new appendix.
 *
 * The sibling of backfillFieldSchema, and it exists because fixing only the
 * schema was not enough. When Appendix 3 shipped, `backfillFieldSchema` added
 * the four itemList fields to every stored template, so the send form grew its
 * hardware editor and happily collected items — but the stored `content` was
 * written before the appendix existed and carries no {{TABLE:…}} markers, so
 * the renderer had nowhere to put them. The contract came out one page shorter
 * with the hardware silently dropped, while Appendix 1 still promised a list of
 * "Specified Devices listed in Appendix 3". Verified on a real envelope: the
 * document had 11 pages and no appendix, and nothing anywhere reported a fault.
 *
 * Content is USER-EDITABLE, so this is strictly additive and never a replace —
 * unlike "Load seed content", which overwrites and asks first. A template whose
 * content already contains the block is returned untouched, so this is safe to
 * run on every load; only a template genuinely missing the appendix is changed,
 * and only by appending. The block goes at the END because the renderer emits
 * the signature block itself, after the content — which is exactly where the
 * seed puts Appendix 3 too.
 */
export const APPENDIX3_MARKER = "<!--APPENDIX3_START-->";
const APPENDIX3_END = "<!--APPENDIX3_END-->";

export function extractAppendix3(seedHtml) {
  const s = String(seedHtml || "");
  const a = s.indexOf(APPENDIX3_MARKER);
  const b = s.indexOf(APPENDIX3_END);
  if (a === -1 || b === -1 || b < a) return null;
  return s.slice(a, b + APPENDIX3_END.length);
}

export function backfillAppendix3(t, seeds) {
  const content = String(t?.content || "");
  // Nothing stored yet: the seed will supply the whole document, appendix and
  // all. Appending to an empty template would produce a contract that is
  // nothing BUT an appendix.
  if (!content.trim()) return t;
  if (content.includes(APPENDIX3_MARKER)) return t;

  const tier = (Array.isArray(t?.appliesToTiers) ? t.appliesToTiers : [])[0];
  const hit = (seeds || []).find((s) => s.tierKey === tier)
           || (seeds || []).find((s) => s.name === t?.name);
  const block = hit && extractAppendix3(hit.content);
  if (!block) return t;                     // seed has no appendix — leave it alone

  return { ...t, content: `${content}\n${block}`, updatedAt: Date.now() };
}

/* Pricing preview for the send form.
 *
 * Deliberately a SEPARATE copy of the arithmetic from the backend's
 * computeContractPricing, and deliberately NOT the source of truth: the backend
 * recomputes from the proposal at send time and its answer is what reaches the
 * contract. This exists only so Craig can see the figures before committing.
 * If the two ever disagree, the backend is right and this is the bug. */
export function computePricingPreview(proposalContent = {}, discount = 0, documentType) {
  const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const r2 = (x) => Math.round(x * 100) / 100;
  const deviceCount = n(proposalContent.deviceCount);
  const userCount = n(proposalContent.userCount);
  const devicePrice = n(proposalContent.perDeviceRate);
  const userPrice = n(proposalContent.perUserRate);
  /* ⚠ MIRRORS computeContractPricing IN THE BACKEND. This is a second copy of
     the same maths, and a preview that disagrees with what is actually sent is
     worse than no preview at all.

     ⚠ It had ALREADY drifted. The backend zeroes the per-user charge on a
     Managed DEVICES document — Bronze's contract has no per-user row, and
     folding one into the subtotal is what once printed $840 of charges a
     client could see no trace of. This copy had no such guard, so the preview
     overstated a Bronze subtotal by exactly that amount while the sent
     document was correct. Fixed here, with the guard now written the same way
     in both places. */
  const isDeviceOnly = documentType === "managed-devices";
  const isCopper = documentType === "copper-devices";

  const copperDeviceCount = n(proposalContent.copperDeviceCount);
  const copperRate = n(proposalContent.copperRate);
  const copperSubtotal = isCopper ? r2(copperDeviceCount * copperRate) : 0;

  const deviceSubtotal = isCopper ? 0 : r2(deviceCount * devicePrice);
  const userSubtotal = isDeviceOnly || isCopper ? 0 : r2(userCount * userPrice);
  const onboardingRate = isDeviceOnly ? 120 : 0;
  const onboardingSubtotal = r2(deviceCount * onboardingRate);
  const grandSubtotal = r2(deviceSubtotal + userSubtotal + onboardingSubtotal
                           + copperSubtotal);
  const disc = r2(n(discount));
  const taxable = r2(grandSubtotal - disc);
  const tax = r2(taxable * 0.10);
  return {
    // Zeroed, not merely excluded, so no figure the document does not charge
    // for can reach the preview either.
    deviceCount: isCopper ? 0 : deviceCount,
    userCount: isDeviceOnly || isCopper ? 0 : userCount,
    devicePrice: isCopper ? 0 : devicePrice,
    userPrice: isDeviceOnly || isCopper ? 0 : userPrice,
    copperDeviceCount: isCopper ? copperDeviceCount : 0,
    copperRate: isCopper ? copperRate : 0,
    copperSubtotal,
    deviceSubtotal, userSubtotal,
    onboardingRate, onboardingSubtotal, grandSubtotal, discount: disc, tax,
    total: r2(taxable + tax),
  };
}

/* ══════════════════════════════════════════════════════════════════════════
   SIGNED WITHOUT DOCUSIGN
   The onboarding pipeline is gated on one instance reaching `signed`, and
   until this existed the only thing that could set it was DocuSign's answer
   to ↻ Refresh status. DocuSign is not configured and Craig may use another
   contract system, which made the whole pipeline unreachable as a side effect.
   These helpers record a signature captured elsewhere. They set status,
   signedAt and provenance on the INSTANCE and nothing else — the onboarding
   record belongs to the PROPOSAL (onboardingRecord.js), and writing one here
   would reintroduce the two-pipelines bug.
   ══════════════════════════════════════════════════════════════════════════ */

export const SIGNED_SOURCES = ["docusign", "manual"];

/**
 * Where a signature came from. Instances written before `signedSource`
 * existed carry no field: those created by the send path have an envelope id
 * and are DocuSign's; anything else without one is unknown. Read defensively,
 * never backfilled.
 */
export function signedSourceOf(ci) {
  if (!ci) return null;
  if (ci.signedSource) return ci.signedSource;
  if (ci.docuSignEnvelopeId) return "docusign";
  return ci.status === "signed" ? "unknown" : null;
}

/**
 * ⚠ Refresh asks DocuSign about an envelope and writes back whatever it says.
 * On a manually-signed instance that is wrong twice over: there is no envelope
 * to ask about, and an answer would overwrite a signature DocuSign never saw —
 * which weeks later reads as "the pipeline disappeared".
 */
export function canRefreshStatus(ci) {
  return !!ci?.docuSignEnvelopeId && signedSourceOf(ci) !== "manual";
}

/** Statuses a contract can still be marked from. */
export const canMarkSigned = (ci) => !!ci && !["signed", "declined", "voided"].includes(ci.status);

/** Only a manual mark is reversible here — a DocuSign signature is DocuSign's to change. */
export const canUnmarkSigned = (ci) => ci?.status === "signed" && signedSourceOf(ci) === "manual";

/** Today as the <input type="date"> value, in LOCAL time (Perth), never UTC. */
export function todayInputValue(now = new Date()) {
  const d = new Date(now);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * The signature date Craig entered, checked and turned into what
 * `signedAtFor()` can read.
 *
 * ⚠ THE DATE IS NOT COSMETIC. signedAtFor takes the EARLIEST signedAt across
 * the proposal's signed contracts and contractDates() makes that the Autotask
 * contract's START DATE. So: it is asked for, never defaulted silently; a
 * future date is rejected (a contract signed tomorrow is a typo); a date
 * before the proposal existed WARNS but does not block (legitimate when
 * backfilling history); and it is stored as an ISO string of local midnight,
 * which `new Date(t)` parses — an unparseable value would be silently
 * dropped by signedAtFor and the contract would date itself from the OTHER
 * envelope, or from nothing.
 *
 * Calendar comparison is done in local time: "2026-09-24" at 7am Perth is
 * 23:00 UTC the day before, and a UTC comparison would call today "future".
 * @returns {{ iso: string|null, error: string|null, warning: string|null }}
 */
export function validateSignedDate(input, { now = new Date(), proposalCreatedAt = null } = {}) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(input || "").trim());
  if (!m) return { iso: null, error: "Enter the date the contract was signed.", warning: null };
  const y = Number(m[1]), mo = Number(m[2]) - 1, d = Number(m[3]);
  const local = new Date(y, mo, d);
  if (local.getFullYear() !== y || local.getMonth() !== mo || local.getDate() !== d) {
    return { iso: null, error: "That is not a real date.", warning: null };
  }
  const n = new Date(now);
  const today = new Date(n.getFullYear(), n.getMonth(), n.getDate());
  if (local.getTime() > today.getTime()) {
    return { iso: null, error: "The signature date is in the future — a contract signed tomorrow is a typo.", warning: null };
  }
  let warning = null;
  if (proposalCreatedAt != null) {
    const created = new Date(proposalCreatedAt);
    if (!Number.isNaN(created.getTime())) {
      const createdDay = new Date(created.getFullYear(), created.getMonth(), created.getDate());
      if (local.getTime() < createdDay.getTime()) {
        warning = `This is before the proposal was created (${createdDay.toLocaleDateString("en-AU")}). Fine if you are backfilling history — check it otherwise.`;
      }
    }
  }
  return { iso: local.toISOString(), error: null, warning };
}

/**
 * The patch that marks an EXISTING instance as signed elsewhere. Provenance
 * travels with it: without `signedSource` nothing could tell a DocuSign
 * signature from a box Craig ticked, and Refresh could not know to stay away.
 * ⛔ Never carries `onboarding` — see the header of this block.
 */
export function markSignedPatch({ signedAtIso, externalRef = "", now = Date.now() } = {}) {
  if (!signedAtIso || Number.isNaN(new Date(signedAtIso).getTime())) throw new Error("markSignedPatch needs a parseable signedAtIso");
  return {
    status: "signed",
    signedAt: signedAtIso,
    signedSource: "manual",
    externalRef: String(externalRef || "").trim() || null,
    lastError: null,
    updatedAt: now,
  };
}

/**
 * Reverse a manual mark. Back to `sent` if the contract had been sent from
 * here, else `draft`. ⚠ Undoes NOTHING the pipeline did — the onboarding
 * record lives on the proposal, so Autotask contracts, tickets and quotes
 * already created stay created; re-marking reveals the panel again with its
 * idempotency intact. The UI says so in its confirm.
 */
export function unmarkSignedPatch(ci, now = Date.now()) {
  return {
    status: ci?.sentAt ? "sent" : "draft",
    signedAt: null,
    signedSource: null,
    externalRef: null,
    updatedAt: now,
  };
}

/**
 * An instance for a contract that never went through the app at all — signed
 * in a third-party system. Created already signed. `docuSignEnvelopeId` and
 * `sentAt` stay null: it was never sent from here and the record must not
 * imply it was.
 */
export function makeSignedElsewhereInstance({ proposalId, template, fieldValues = {}, signedAtIso, externalRef = "", now = Date.now() } = {}) {
  const base = makeContractInstance({ proposalId, contractTemplateId: template?.id || null, fieldValues });
  return {
    ...base,
    templateNameAtSend: template?.name || "",
    ...markSignedPatch({ signedAtIso, externalRef, now }),
    createdAt: now,
  };
}
